import { useParams, useNavigate } from 'react-router-dom';
import { useWorkflowStore } from '../store/workflowStore';
import { useBusinessSettingsStore } from '../store/businessSettingsStore';
import { ArrowLeft, Printer, Download, Shield, Building2 } from 'lucide-react';

const fmtCurrency = (n: number) =>
  `Rs. ${n.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString('en-LK', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

export function InvoiceDocument({ invoiceId }: { invoiceId: string }) {
  const { invoices, warranties } = useWorkflowStore();
  const { settings: biz } = useBusinessSettingsStore();
  const invoice = invoices.find(i => i.id === invoiceId);
  const warranty = warranties.find(w => w.invoiceId === invoiceId);

  if (!invoice) return null;

  const warrantyUrl = warranty
    ? `${window.location.origin}/warranty/verify/${warranty.warrantyId}`
    : null;
  const qrUrl = warrantyUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(warrantyUrl)}`
    : null;

  return (
    <div
      id={`invoice-doc-${invoiceId}`}
      className="bg-white w-[794px] min-h-[1123px] print:w-full print:min-h-0 p-10"
      style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}
    >
      {/* ─── HEADER ─────────────────────────────────────────── */}
      <div className="flex items-start justify-between mb-8">
        {/* Company Info */}
        <div>
          <div className="flex items-center gap-3 mb-3">
            {biz.logo ? (
              <img src={biz.logo} alt="Logo" className="w-14 h-14 object-contain rounded-lg" />
            ) : (
              <div className="w-14 h-14 bg-[#7FA86A] rounded-xl flex items-center justify-center">
                <Building2 className="w-7 h-7 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-xl font-bold text-gray-900 uppercase tracking-wide">{biz.businessName}</h1>
              <p className="text-xs text-gray-500">{biz.tagline}</p>
            </div>
          </div>
          <div className="text-xs text-gray-500 space-y-0.5">
            {biz.address && <p>📍 {biz.address}</p>}
            {biz.phone && <p>📞 {biz.phone}</p>}
            {biz.email && <p>✉ {biz.email}</p>}
            {biz.website && <p>🌐 {biz.website}</p>}
          </div>
        </div>

        {/* Invoice Meta */}
        <div className="text-right">
          <div className="inline-block bg-[#7FA86A] text-white px-6 py-2 rounded-xl mb-3">
            <h2 className="text-2xl font-bold tracking-widest">INVOICE</h2>
          </div>
          <div className="text-sm space-y-1.5">
            <div className="flex justify-end gap-8">
              <span className="text-gray-500 font-medium">Invoice No.</span>
              <span className="font-bold text-gray-900 font-mono">{invoice.invoiceNumber}</span>
            </div>
            <div className="flex justify-end gap-8">
              <span className="text-gray-500">Invoice Date</span>
              <span className="text-gray-800">{fmtDate(invoice.invoiceDate)}</span>
            </div>
            {invoice.installationDate && (
              <div className="flex justify-end gap-8">
                <span className="text-gray-500">Installation Date</span>
                <span className="text-gray-800">{fmtDate(invoice.installationDate)}</span>
              </div>
            )}
            <div className="flex justify-end gap-8 mt-2">
              <span className="text-gray-500">Payment Status</span>
              <span className={`px-3 py-0.5 rounded-full text-xs font-semibold capitalize ${
                invoice.paymentStatus === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                invoice.paymentStatus === 'partial' ? 'bg-yellow-100 text-yellow-700' :
                'bg-red-100 text-red-700'
              }`}>
                {invoice.paymentStatus}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ─── DIVIDER ────────────────────────────────────────── */}
      <div className="h-0.5 bg-gradient-to-r from-[#7FA86A] to-transparent mb-7" />

      {/* ─── BILL TO ────────────────────────────────────────── */}
      <div className="mb-7 grid grid-cols-2 gap-6">
        <div>
          <p className="text-[10px] font-bold text-[#7FA86A] uppercase tracking-widest mb-2">Bill To</p>
          <div className="bg-[#F1F6EE] rounded-xl p-4">
            <p className="text-base font-bold text-gray-900">{invoice.customerName}</p>
            <p className="text-sm text-gray-600 mt-0.5">{invoice.customerPhone}</p>
            {invoice.customerEmail && <p className="text-sm text-gray-500">{invoice.customerEmail}</p>}
            {invoice.customerAddress && <p className="text-sm text-gray-500 mt-1">{invoice.customerAddress}</p>}
          </div>
        </div>
        {(invoice.productWarranty || invoice.serviceWarranty) && (
          <div>
            <p className="text-[10px] font-bold text-purple-600 uppercase tracking-widest mb-2">Warranty</p>
            <div className="bg-purple-50 rounded-xl p-4">
              {invoice.productWarranty && <p className="text-sm text-purple-700">🛡 Product: {invoice.productWarranty}</p>}
              {invoice.serviceWarranty && <p className="text-sm text-purple-600 mt-1">🔧 Service: {invoice.serviceWarranty}</p>}
            </div>
          </div>
        )}
      </div>

      {/* ─── ITEMS TABLE ────────────────────────────────────── */}
      <table className="w-full text-sm mb-7">
        <thead>
          <tr style={{ background: '#1a1a1a', color: 'white' }}>
            <th className="text-left px-3 py-3 rounded-tl-lg text-xs">#</th>
            <th className="text-left px-3 py-3 text-xs">Description</th>
            <th className="text-left px-3 py-3 text-xs">Model</th>
            <th className="text-left px-3 py-3 text-xs">Warranty</th>
            <th className="text-right px-3 py-3 text-xs">Qty</th>
            <th className="text-right px-3 py-3 text-xs">Unit Price</th>
            <th className="text-right px-3 py-3 rounded-tr-lg text-xs">Total</th>
          </tr>
        </thead>
        <tbody>
          {invoice.lineItems.map((item, i) => (
            <tr key={item.id} style={{ background: i % 2 === 0 ? '#ffffff' : '#F1F6EE' }}>
              <td className="px-3 py-3 text-gray-400 text-xs">{i + 1}</td>
              <td className="px-3 py-3">
                <div className="font-semibold text-gray-900">{item.name}</div>
                {item.description && <div className="text-xs text-gray-500 mt-0.5">{item.description}</div>}
              </td>
              <td className="px-3 py-3 text-gray-600 text-xs">{item.modelNumber || '—'}</td>
              <td className="px-3 py-3 text-gray-600 text-xs">{item.warrantyPeriod || '—'}</td>
              <td className="px-3 py-3 text-right text-xs">{item.quantity} {item.unit || 'Pcs'}</td>
              <td className="px-3 py-3 text-right text-xs">{fmtCurrency(item.unitPrice)}</td>
              <td className="px-3 py-3 text-right font-semibold text-xs">{fmtCurrency(item.lineTotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ─── TOTALS + QR ────────────────────────────────────── */}
      <div className="flex items-start justify-between mb-7 gap-8">
        {/* QR Code */}
        <div className="flex-shrink-0 text-center">
          {qrUrl ? (
            <div>
              <img src={qrUrl} alt="Warranty QR Code" className="w-32 h-32 border-2 border-[#D8E7D1] rounded-xl" />
              <p className="text-[10px] text-gray-500 mt-1.5 font-medium">Scan for Warranty</p>
              <p className="text-[9px] font-mono text-gray-400 mt-0.5">{warranty?.warrantyId}</p>
            </div>
          ) : (
            <div className="w-32 h-32 bg-gray-100 rounded-xl flex flex-col items-center justify-center text-xs text-gray-400 border-2 border-dashed border-gray-200">
              <Shield className="w-6 h-6 mb-1 opacity-40" />
              <span className="text-center leading-tight">Warranty QR<br />after payment</span>
            </div>
          )}
        </div>

        {/* Totals */}
        <div className="flex-1 max-w-xs ml-auto">
          <table className="w-full text-sm">
            <tbody>
              <tr>
                <td className="py-1.5 text-gray-500">Subtotal</td>
                <td className="py-1.5 text-right font-medium">{fmtCurrency(invoice.subtotal)}</td>
              </tr>
              {invoice.itemDiscount > 0 && (
                <tr>
                  <td className="py-1.5 text-red-500">Item Discounts</td>
                  <td className="py-1.5 text-right text-red-500">- {fmtCurrency(invoice.itemDiscount)}</td>
                </tr>
              )}
              {invoice.overallDiscount > 0 && (
                <tr>
                  <td className="py-1.5 text-red-500">Discount</td>
                  <td className="py-1.5 text-right text-red-500">- {fmtCurrency(
                    invoice.overallDiscountType === 'percentage'
                      ? invoice.subtotal * (invoice.overallDiscount / 100)
                      : invoice.overallDiscount
                  )}</td>
                </tr>
              )}
              {invoice.installationCost > 0 && (
                <tr>
                  <td className="py-1.5 text-gray-500">Installation</td>
                  <td className="py-1.5 text-right">{fmtCurrency(invoice.installationCost)}</td>
                </tr>
              )}
              {invoice.labourCost > 0 && (
                <tr>
                  <td className="py-1.5 text-gray-500">Labour</td>
                  <td className="py-1.5 text-right">{fmtCurrency(invoice.labourCost)}</td>
                </tr>
              )}
              {invoice.additionalCharges > 0 && (
                <tr>
                  <td className="py-1.5 text-gray-500">Additional Charges</td>
                  <td className="py-1.5 text-right">{fmtCurrency(invoice.additionalCharges)}</td>
                </tr>
              )}
              {invoice.taxRate > 0 && (
                <tr>
                  <td className="py-1.5 text-gray-500">Tax ({invoice.taxRate}%)</td>
                  <td className="py-1.5 text-right">{fmtCurrency(invoice.taxAmount)}</td>
                </tr>
              )}
              <tr className="border-t-2 border-gray-900">
                <td className="py-2 font-bold text-base">Grand Total</td>
                <td className="py-2 text-right font-bold text-xl text-[#7FA86A]">{fmtCurrency(invoice.grandTotal)}</td>
              </tr>
              <tr>
                <td className="py-1.5 text-emerald-600 font-medium">Paid Amount</td>
                <td className="py-1.5 text-right text-emerald-600 font-medium">{fmtCurrency(invoice.totalPaid)}</td>
              </tr>
              <tr>
                <td className="py-1.5 font-bold text-red-600">Balance Due</td>
                <td className="py-1.5 text-right font-bold text-red-600">{fmtCurrency(invoice.balance)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ─── PAYMENT HISTORY ────────────────────────────────── */}
      {invoice.payments.length > 0 && (
        <div className="mb-7">
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2">Payment History</p>
          <div className="bg-gray-50 rounded-xl overflow-hidden">
            {invoice.payments.map(p => (
              <div key={p.id} className="flex justify-between items-center px-4 py-2.5 border-b border-gray-100 last:border-0 text-sm">
                <div>
                  <span className="font-medium capitalize">{p.method.replace('_', ' ')}</span>
                  {p.reference && <span className="text-gray-400 ml-2 text-xs">· {p.reference}</span>}
                  <div className="text-xs text-gray-400">{fmtDate(p.date)}</div>
                </div>
                <span className="text-emerald-600 font-semibold">{fmtCurrency(p.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── NOTES ───────────────────────────────────────────── */}
      {invoice.notes && (
        <div className="bg-blue-50 rounded-xl p-4 mb-5">
          <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mb-1">Notes</p>
          <p className="text-sm text-blue-700">{invoice.notes}</p>
        </div>
      )}

      {/* ─── TERMS ───────────────────────────────────────────── */}
      {invoice.termsAndConditions && (
        <div className="border-t border-gray-100 pt-4 mb-5 text-xs text-gray-400">
          <p className="font-semibold text-gray-500 mb-1">Terms & Conditions</p>
          <p>{invoice.termsAndConditions}</p>
        </div>
      )}

      {/* ─── FOOTER ──────────────────────────────────────────── */}
      <div className="text-center text-xs text-gray-400 border-t border-[#D8E7D1] pt-4 mt-6">
        <p className="font-medium text-[#4A6741]">Thank you for choosing {biz.businessName}</p>
        <p className="mt-0.5">{biz.website && `${biz.website} · `}{biz.email && `${biz.email} · `}{biz.phone}</p>
      </div>
    </div>
  );
}

export default function InvoicePrintPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { invoices, warranties } = useWorkflowStore();
  const invoice = invoices.find(i => i.id === id);

  if (!id || !invoice) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500">Invoice not found.</p>
          <button onClick={() => navigate('/admin/invoices')} className="mt-4 px-4 py-2 bg-gray-100 rounded-xl text-sm">
            Back to Invoices
          </button>
        </div>
      </div>
    );
  }

  const warranty = warranties.find(w => w.invoiceId === id);
  const warrantyUrl = warranty
    ? `${window.location.origin}/warranty/verify/${warranty.warrantyId}`
    : null;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Controls bar (hidden on print) */}
      <div className="print:hidden bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-3 flex-wrap">
        <button onClick={() => navigate('/admin/invoices')} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="ml-auto flex gap-2 flex-wrap">
          {warrantyUrl && (
            <button
              onClick={() => window.open(warrantyUrl, '_blank')}
              className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-xl text-sm font-medium hover:bg-purple-100"
            >
              <Shield className="w-4 h-4" /> Warranty QR
            </button>
          )}
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200"
          >
            <Printer className="w-4 h-4" /> Print
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-2 bg-[#7FA86A] text-white rounded-xl text-sm font-medium hover:bg-[#658C53]"
          >
            <Download className="w-4 h-4" /> Download PDF
          </button>
        </div>
      </div>

      {/* A4 Preview */}
      <div className="flex justify-center py-8 print:py-0 print:block">
        <div className="shadow-lg print:shadow-none">
          <InvoiceDocument invoiceId={id} />
        </div>
      </div>

      <style>{`
        @media print {
          body { margin: 0; background: white; }
          .print\\:hidden { display: none !important; }
          .print\\:py-0 { padding-top: 0 !important; padding-bottom: 0 !important; }
          .print\\:block { display: block !important; }
          .print\\:shadow-none { box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}
